import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './style.css';
import { track } from '@vercel/analytics';

import { ENV } from '@/lib/apiConfig';
import {
  extractAuthToken,
  fetchAuthMeAfterLogin,
  getPortalLoginErrorMessage,
  getStaffEntryPath,
  isStaffUser,
  loginWithCredentials,
} from '@/services/authSession';
import { getAccountDataByCNPJ, getContactByEmail } from '@/services/xanoSalesforce';
import { appToast } from '@/utils/appToast';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Quando true, não fecha ao clicar fora nem com Escape (login obrigatório no portal). */
  mandatory?: boolean;
  onOpenRegister?: () => void;
  onOpenForgotPassword?: () => void;
  onLoginSuccess?: () => void;
}

const LoginModal = ({
  isOpen,
  onClose,
  mandatory = false,
  onOpenRegister,
  onOpenForgotPassword,
  onLoginSuccess,
}: LoginModalProps) => {
  const navigate = useNavigate();
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    general: '',
  });

  const handleClose = () => {
    setLoginData({ email: '', password: '' });
    setErrors({ email: '', password: '', general: '' });
    setIsLoading(false);
    onClose();
  };

  // Função para validar email
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Função para validar campos
  const validateFields = (): boolean => {
    const newErrors = { email: '', password: '', general: '' };
    let isValid = true;

    if (!loginData.email.trim()) {
      newErrors.email = 'Email é obrigatório';
      isValid = false;
    } else if (!validateEmail(loginData.email)) {
      newErrors.email = 'Email inválido';
      isValid = false;
    }

    if (!loginData.password.trim()) {
      newErrors.password = 'Senha é obrigatória';
      isValid = false;
    } else if (loginData.password.length < 6) {
      newErrors.password = 'Senha deve ter pelo menos 6 caracteres';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Limpa erros anteriores
    setErrors({ email: '', password: '', general: '' });

    // Valida campos
    if (!validateFields()) {
      return;
    }

    setIsLoading(true);

    try {
      // Faz o login na API
      const response = await loginWithCredentials(
        loginData.email,
        loginData.password
      );
      const authToken = extractAuthToken(response);

      // Armazena o token no localStorage
      if (authToken) {
        localStorage.setItem('authToken', authToken);
        localStorage.setItem('userEmail', loginData.email);

        // Entra no portal assim que o token chega — auth/me não pode bloquear
        window.dispatchEvent(new CustomEvent('user-logged-in'));
        onLoginSuccess?.();
        handleClose();

        appToast.success('Login realizado com sucesso!');

        track('Login Realizado', { email: loginData.email });

        void (async () => {
          try {
            const me = await fetchAuthMeAfterLogin();

            if (isStaffUser(me)) {
              track('Login Realizado', { email: loginData.email, backoffice: true });
              if (me.must_change_password) {
                appToast.info('Primeiro acesso: defina uma nova senha para continuar.');
                navigate(getStaffEntryPath(me), { replace: true });
              }
              return;
            }

            const personType = me?.person_type || 'PF';
            const onlyDigits = (s: string) => (s || '').replace(/\D/g, '');

            if (personType === 'PF') {
              try {
                await getContactByEmail(loginData.email);
                if (ENV === 'dev') {
                  // eslint-disable-next-line no-console
                  console.info('[login] PF: verificado contact para', loginData.email);
                }
              } catch {
                if (ENV === 'dev') {
                  // eslint-disable-next-line no-console
                  console.info('[login] PF: contact não encontrado para', loginData.email);
                }
              }
            }

            const possibleCNPJ =
              me?.cnpj ||
              me?.CNPJ ||
              me?.company_document ||
              me?.tax_id ||
              me?.document ||
              '';
            const cnpj = onlyDigits(possibleCNPJ);

            let wallet = 'GOLD';
            if (cnpj && cnpj.length >= 14) {
              try {
                const acc = await getAccountDataByCNPJ(cnpj);
                const tier = (acc?.CE_Carteira_c || acc?.ce_carteira_c || '')
                  .toString()
                  .trim();
                wallet = tier ? tier.toUpperCase() : 'GOLD';
              } catch {
                // mantém GOLD
              }
            }
            localStorage.setItem('wallet', wallet);

            if (ENV === 'dev') {
              // eslint-disable-next-line no-console
              console.info('[login] cnpj=', cnpj || '(none)', 'wallet=', wallet);
            }

            if (me?.internal_disabled && me?.backoffice_role) {
              appToast.info(
                'Seu acesso ao backoffice está desativado. Você entrou como cliente no portal.',
                { autoClose: 5000 }
              );
            }
          } catch {
            localStorage.setItem('wallet', 'GOLD');
          }
        })();
      } else {
        throw new Error('Token de autenticação não recebido');
      }
    } catch (error) {
      const errorMessage = getPortalLoginErrorMessage(error);

      // Define erro geral
      setErrors(prev => ({
        ...prev,
        general: errorMessage,
      }));

      // Não mostra toast para credenciais inválidas, apenas para outros erros
      const normalizedError = errorMessage.toLowerCase();
      const isCredentialError =
        normalizedError.includes('invalid') ||
        normalizedError.includes('credencial') ||
        normalizedError.includes('senha incorret') ||
        normalizedError.includes('e-mail ou senha');

      if (!isCredentialError) {
        appToast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!mandatory || !isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [mandatory, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Limpa erro do campo específico quando usuário digita
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
        general: '', // Limpa erro geral também
      }));
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={`login-modal-overlay${mandatory ? ' login-modal-overlay--mandatory' : ''}`}
          onClick={undefined}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className='login-modal-container'
            onClick={e => e.stopPropagation()}
            initial={{ opacity: 0, y: -30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -30, scale: 0.9 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <motion.div
              className='login-modal-header'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut', delay: 0.1 }}
            >
              <h2 className='login-modal-title'>Bem-vindo (a)!</h2>
              <p className='login-modal-subtitle'>
                Entre na sua conta para continuar
              </p>
            </motion.div>

            <motion.form
              onSubmit={handleSubmit}
              className='login-modal-form'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut', delay: 0.2 }}
            >
              <motion.div
                className='login-form-group'
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, ease: 'easeOut', delay: 0.3 }}
              >
                <label htmlFor='email' className='login-form-label'>
                  Email
                </label>
                <input
                  type='email'
                  id='email'
                  name='email'
                  value={loginData.email}
                  onChange={handleInputChange}
                  className={`login-form-input ${errors.email ? 'login-form-input--error' : ''}`}
                  placeholder='seu@email.com'
                  disabled={isLoading}
                  required
                />
                {errors.email && (
                  <span className='login-form-error'>{errors.email}</span>
                )}
              </motion.div>

              <motion.div
                className='login-form-group'
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, ease: 'easeOut', delay: 0.4 }}
              >
                <label htmlFor='password' className='login-form-label'>
                  Senha
                </label>
                <input
                  type='password'
                  id='password'
                  name='password'
                  value={loginData.password}
                  onChange={handleInputChange}
                  className={`login-form-input ${errors.password ? 'login-form-input--error' : ''}`}
                  placeholder='Sua senha'
                  disabled={isLoading}
                  required
                />
                {errors.password && (
                  <span className='login-form-error'>{errors.password}</span>
                )}
              </motion.div>

              {errors.general && (
                <motion.div
                  className='login-form-error-general'
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {errors.general}
                </motion.div>
              )}

              <motion.div
                className='login-form-actions'
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, ease: 'easeOut', delay: 0.5 }}
              >
                <button
                  type='submit'
                  className={`login-btn-primary ${isLoading ? 'login-btn-primary--loading' : ''}`}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <div className='login-spinner'></div>
                      Entrando...
                    </>
                  ) : (
                    'Entrar'
                  )}
                </button>
                <button
                  type='button'
                  onClick={onOpenForgotPassword}
                  className='login-btn-link'
                  disabled={isLoading}
                >
                  Esqueci minha senha
                </button>
              </motion.div>
            </motion.form>

            <motion.div
              className='login-modal-footer'
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut', delay: 0.6 }}
            >
              <p className='login-register-text'>
                Não tem uma conta?{' '}
                <button
                  type='button'
                  onClick={onOpenRegister}
                  className='login-register-link'
                >
                  Cadastre-se
                </button>
              </p>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoginModal;
